import type { GhostDeviceItem } from '../../utils/halfDepth.logic';
import { TYPE_COLORS, TYPE_NAMES_RU } from './equipmentTypes';

interface GridGhostEquipmentProps {
  ghost: GhostDeviceItem;
  uSlots: number;
  isDark?: boolean;
  onSlotClick?: (u: number, mount: string) => void;
  isMountingMode?: boolean;
}

/**
 * Renders a subtle "Ghost" outline for an equipment item installed on the OPPOSITE face of the rack.
 * E.g., when viewing Front, renders a rear half-depth device outline with [ ТЫЛ: {name} (Half-Depth) ].
 * Allows mounting in the unoccupied face while clearly warning the engineer of depth limitations.
 */
export function GridGhostEquipment({
  ghost,
  uSlots,
  isDark = true,
  onSlotClick,
  isMountingMode,
}: GridGhostEquipmentProps) {
  const { eq, oppositeFace, u_position, u_height } = ghost;
  const rowStart = uSlots - u_position - u_height + 2;

  const isRear = oppositeFace === 'rear';
  const faceLabel = isRear ? 'ТЫЛ' : 'ФРОНТ';
  const currentFace = isRear ? 'Front' : 'Rear';

  const typeColor = TYPE_COLORS[eq.equipment_type] || TYPE_COLORS.unknown;
  const typeNameRu = TYPE_NAMES_RU[eq.equipment_type] || eq.equipment_type || 'Устройство';

  const rangeEnd = u_position + u_height - 1;
  const rangeStr = u_height > 1 ? `U${u_position}..U${rangeEnd}` : `U${u_position}`;

  const tooltip = `${faceLabel === 'ТЫЛ' ? 'Тыльное' : 'Фронтальное'} оборудование: '${eq.name}' (${typeNameRu}, ${rangeStr}, ${u_height}U, половинная глубина).\n${isRear ? 'Фронтальная' : 'Тыльная'} сторона свободна для монтажа полуглубинных устройств.`;

  return (
    <div
      style={{
        gridRow: `${rowStart} / span ${u_height}`,
        gridColumn: 2, // Always takes single slot column in Front or Rear projection
      }}
      title={tooltip}
      onClick={() => isMountingMode && onSlotClick?.(u_position, currentFace)}
      className={`relative z-[5] border border-dashed rounded-[2px] transition-all flex items-center justify-between px-2 font-mono select-none ${
        isMountingMode ? 'cursor-pointer' : 'cursor-default'
      } ${
        isRear
          ? isDark
            ? 'border-indigo-500/40 bg-indigo-950/20 hover:border-indigo-400 hover:bg-indigo-950/30'
            : 'border-indigo-400/60 bg-indigo-50/40 hover:border-indigo-500 hover:bg-indigo-50/60'
          : isDark
            ? 'border-cyan-500/40 bg-cyan-950/20 hover:border-cyan-400 hover:bg-cyan-950/30'
            : 'border-cyan-400/60 bg-cyan-50/40 hover:border-cyan-500 hover:bg-cyan-50/60'
      }`}
    >
      <div className="flex items-center gap-1.5 min-w-0 truncate">
        {/* Ghost badge */}
        <span
          className={`px-1 py-0.5 rounded text-[8px] font-bold tracking-wider uppercase border shrink-0 ${
            isRear
              ? isDark
                ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40'
                : 'bg-indigo-100 text-indigo-700 border-indigo-300'
              : isDark
                ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                : 'bg-cyan-100 text-cyan-700 border-cyan-300'
          }`}
        >
          [ {faceLabel}: {eq.name} (Half-Depth) ]
        </span>

        {/* Equipment type indicator dot */}
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0 opacity-75"
          style={{ backgroundColor: typeColor }}
          aria-hidden="true"
        />

        <span
          className={`text-[9px] truncate ${
            isDark ? 'text-slate-400' : 'text-slate-500'
          }`}
        >
          {typeNameRu}
        </span>
      </div>

      <div className="flex items-center gap-1.5 shrink-0 pl-1 text-[8px] font-bold">
        <span
          className={
            isRear
              ? isDark ? 'text-indigo-400/80' : 'text-indigo-600/80'
              : isDark ? 'text-cyan-400/80' : 'text-cyan-600/80'
          }
        >
          {rangeStr} ({u_height}U)
        </span>
      </div>
    </div>
  );
}
